/**
 * External transcoder boundary (milestone five).
 *
 * PRISM talks to external transcoding systems only through this contract. An
 * adapter translates it to a product's own API; nothing else in PRISM knows
 * which product is behind it. Media is exchanged through storage both sides
 * can reach (a shared filesystem path or a pre-signed URL in, a shared output
 * folder out), which is how on-premises transcoders are normally deployed.
 *
 * The HTTP form below ("PRISM transcoder protocol v1") is what the bundled
 * simulator speaks. It is PRISM's own protocol, not any vendor's API.
 */
import { z } from 'zod';

export const TranscoderProfileSchema = z.object({
  /** Stable id used in workflow graphs. */
  id: z.string().regex(/^[a-z0-9][a-z0-9._-]{0,63}$/),
  label: z.string(),
  description: z.string().default(''),
  /** Output container and codec, for display and for the rendition's MIME type. */
  container: z.string(),
  mimeType: z.string(),
  videoCodec: z.string().nullable(),
  audioCodec: z.string().nullable(),
  extension: z.string().regex(/^[a-z0-9]{1,8}$/),
});
export type TranscoderProfile = z.infer<typeof TranscoderProfileSchema>;

export const TranscoderInfoSchema = z.object({
  engine: z.string(),
  /** Human-readable product name, e.g. "PRISM transcoder simulator". */
  product: z.string(),
  version: z.string(),
  /** True for the bundled simulator; the UI labels it as such everywhere. */
  simulated: z.boolean(),
  profiles: z.array(TranscoderProfileSchema),
});
export type TranscoderInfo = z.infer<typeof TranscoderInfoSchema>;

export const TranscodeSubmissionSchema = z.object({
  /** Idempotency key chosen by PRISM (run and step). Submitting the same key again returns the existing job. */
  clientJobId: z.string().min(1).max(200),
  profileId: z.string(),
  /** Where the transcoder reads the source: a path on storage it shares with PRISM, or a pre-signed URL. */
  source: z.union([z.object({ path: z.string().min(1) }), z.object({ url: z.string().url() })]),
  /** Output name (no directories); the transcoder writes it into its shared output folder. */
  outputName: z.string().regex(/^[\w.-]{1,200}$/),
  /** Free-form labels passed through for the operator (asset title, run id). */
  labels: z.record(z.string(), z.string()).default({}),
});
export type TranscodeSubmission = z.infer<typeof TranscodeSubmissionSchema>;

export const ExternalJobStateSchema = z.enum(['queued', 'running', 'succeeded', 'failed', 'cancelled']);
export type ExternalJobState = z.infer<typeof ExternalJobStateSchema>;

export const ExternalJobSchema = z.object({
  id: z.string(),
  clientJobId: z.string(),
  profileId: z.string(),
  state: ExternalJobStateSchema,
  percent: z.number().min(0).max(100).nullable(),
  /** Relative to the transcoder's shared output folder. */
  output: z.object({ path: z.string(), sizeBytes: z.number().int().nonnegative() }).nullable(),
  error: z.object({ code: z.string(), message: z.string(), retryable: z.boolean() }).nullable(),
  submittedAt: z.string(),
  updatedAt: z.string(),
});
export type ExternalJob = z.infer<typeof ExternalJobSchema>;
